import { useState, useEffect, useMemo } from 'react';
import ClientShell from './ClientShell';
import { clientFetch } from './api';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#FFF',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#8B92A5',
  },
  controlsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  tabsContainer: {
    display: 'flex',
    gap: '8px',
    borderBottom: '1px solid #2A3444',
    marginBottom: '24px',
  },
  tab: {
    padding: '12px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#8B92A5',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#FFF',
    borderBottomColor: '#0057FF',
  },
  badge: {
    display: 'inline-block',
    backgroundColor: '#FF6B6B',
    color: '#FFF',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '6px',
  },
  markAllButton: {
    backgroundColor: '#1A2332',
    color: '#0057FF',
    border: '1px solid #2A3444',
    padding: '10px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  markAllButtonHover: {
    backgroundColor: '#2A3444',
    borderColor: '#0057FF',
  },
  notificationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  notificationItem: {
    backgroundColor: '#0F1729',
    border: '1px solid #2A3444',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    gap: '12px',
    transition: 'all 0.2s',
    cursor: 'pointer',
    position: 'relative',
  },
  notificationItemUnread: {
    borderColor: '#0057FF',
    backgroundColor: '#1A2332',
  },
  notificationIcon: {
    fontSize: '24px',
    minWidth: '32px',
    textAlign: 'center',
    flexShrink: 0,
  },
  notificationContent: {
    flex: 1,
    minWidth: 0,
  },
  notificationTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '4px',
  },
  notificationMessage: {
    fontSize: '13px',
    color: '#BBBEC7',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  notificationMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    color: '#8B92A5',
  },
  notificationTime: {
    fontSize: '12px',
    color: '#8B92A5',
  },
  notificationAction: {
    padding: '6px 12px',
    backgroundColor: '#0057FF',
    color: '#FFF',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  notificationActionHover: {
    backgroundColor: '#0047D4',
  },
  unreadBadge: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '8px',
    height: '8px',
    backgroundColor: '#0057FF',
    borderRadius: '50%',
  },
  promotionCard: {
    backgroundColor: '#0F1729',
    border: '1px solid #2A3444',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    display: 'grid',
    gridTemplateColumns: '120px 1fr auto',
    gap: '16px',
    alignItems: 'center',
    transition: 'all 0.2s',
  },
  promotionCardHover: {
    borderColor: '#00D4AA',
    backgroundColor: '#1A2332',
  },
  promotionIcon: {
    fontSize: '48px',
    textAlign: 'center',
  },
  promotionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  promotionTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#FFF',
  },
  promotionDescription: {
    fontSize: '13px',
    color: '#BBBEC7',
  },
  promotionCTA: {
    padding: '10px 16px',
    backgroundColor: '#00D4AA',
    color: '#0A0F1D',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  promotionCTAHover: {
    backgroundColor: '#00B89C',
    transform: 'translateY(-2px)',
  },
  preferencesSection: {
    backgroundColor: '#0F1729',
    border: '1px solid #2A3444',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '32px',
  },
  preferencesTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#FFF',
    marginBottom: '16px',
  },
  preferenceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '16px',
    borderBottom: '1px solid #2A3444',
  },
  preferenceRowLast: {
    borderBottom: 'none',
  },
  preferenceLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#FFF',
  },
  preferenceDescription: {
    fontSize: '12px',
    color: '#8B92A5',
    marginTop: '4px',
  },
  toggle: {
    position: 'relative',
    display: 'inline-block',
    width: '48px',
    height: '28px',
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  toggleSlider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#2A3444',
    transition: 'all 0.2s',
    borderRadius: '14px',
  },
  toggleSliderOn: {
    backgroundColor: '#0057FF',
  },
  toggleDot: {
    position: 'absolute',
    content: '""',
    height: '24px',
    width: '24px',
    left: '2px',
    bottom: '2px',
    backgroundColor: '#FFF',
    transition: 'all 0.2s',
    borderRadius: '50%',
  },
  toggleDotOn: {
    transform: 'translateX(20px)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 24px',
    color: '#8B92A5',
  },
  emptyStateIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },
  emptyStateTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#FFF',
    marginBottom: '8px',
  },
  emptyStateText: {
    fontSize: '14px',
    color: '#8B92A5',
  },
  loadingText: {
    color: '#8B92A5',
    fontSize: '14px',
    padding: '24px',
    textAlign: 'center',
  },
  errorText: {
    color: '#FF6B6B',
    fontSize: '14px',
    padding: '24px',
    textAlign: 'center',
  },
};

// Hardcoded notification data
const MOCK_NOTIFICATIONS = [
  {
    id: 'alert-1',
    type: 'alert',
    category: 'alerts',
    icon: '⚠️',
    title: 'Service Maintenance Scheduled',
    message: 'Planned maintenance on your NBN connection scheduled for Sunday 2-4 AM AEST.',
    timestamp: '2 hours ago',
    read: false,
    action: { label: 'View Details', callback: 'viewMaintenance' },
  },
  {
    id: 'promo-1',
    type: 'marketing',
    category: 'marketing',
    icon: '🎉',
    title: 'New Service: Advanced Cloud Backup',
    message: 'Protect your data with our new enterprise-grade cloud backup solution. Get 20% off for the first 3 months.',
    timestamp: '5 hours ago',
    read: false,
    action: { label: 'Learn More', callback: 'learnMore' },
  },
  {
    id: 'ticket-1',
    type: 'alert',
    category: 'alerts',
    icon: '🎫',
    title: 'Your Support Ticket #2847 Has a Response',
    message: 'Our team has responded to your VoIP configuration issue. Check the update in your support portal.',
    timestamp: '8 hours ago',
    read: false,
    action: { label: 'View Ticket', callback: 'viewTicket' },
  },
  {
    id: 'update-1',
    type: 'update',
    category: 'updates',
    icon: '📢',
    title: 'Portal Update v2.4.1 Released',
    message:
      'New dashboard features, improved security, and performance optimizations are now live.',
    timestamp: '1 day ago',
    read: true,
    action: null,
  },
  {
    id: 'promo-2',
    type: 'marketing',
    category: 'marketing',
    icon: '💼',
    title: 'Upgrade to Mid-Market Plan',
    message:
      'Your business is growing! Upgrade to our Mid-Market tier and unlock advanced networking features.',
    timestamp: '1 day ago',
    read: true,
    action: { label: 'Upgrade Plan', callback: 'upgradePlan' },
  },
  {
    id: 'system-1',
    type: 'system',
    category: 'system',
    icon: '🔒',
    title: 'Security Patch Applied',
    message:
      'Critical security update has been applied to your account. No action required.',
    timestamp: '2 days ago',
    read: true,
    action: null,
  },
  {
    id: 'billing-1',
    type: 'alert',
    category: 'alerts',
    icon: '💰',
    title: 'Invoice #INV-2024-4521 Ready',
    message: 'Your monthly invoice is now available for download in your Invoices section.',
    timestamp: '3 days ago',
    read: true,
    action: { label: 'Download', callback: 'downloadInvoice' },
  },
  {
    id: 'promo-3',
    type: 'marketing',
    category: 'marketing',
    icon: '📱',
    title: 'Mobile App Now Available',
    message:
      'Stay connected on the go with the new Talk Australia mobile app for iOS and Android.',
    timestamp: '3 days ago',
    read: true,
    action: { label: 'Download App', callback: 'downloadApp' },
  },
  {
    id: 'system-2',
    type: 'system',
    category: 'system',
    icon: '✓',
    title: 'Backup Completed Successfully',
    message: 'Your automatic daily backup completed at 2:30 AM with 2.4 GB of data backed up.',
    timestamp: '4 days ago',
    read: true,
    action: null,
  },
  {
    id: 'update-2',
    type: 'update',
    category: 'updates',
    icon: '🚀',
    title: 'Feature Preview: AI-Powered Security',
    message:
      'Coming soon - Advanced threat detection powered by machine learning to better protect your business.',
    timestamp: '5 days ago',
    read: true,
    action: { label: 'Learn More', callback: 'learnMore' },
  },
];

const PROMOTIONS = [
  {
    id: 'promo-card-1',
    icon: '☁️',
    title: 'Cloud Backup Pro',
    description: 'Unlimited cloud storage with military-grade encryption',
    cta: 'Upgrade Now',
  },
  {
    id: 'promo-card-2',
    icon: '🛡️',
    title: 'Managed Security Services',
    description: '24/7 SOC monitoring and threat intelligence',
    cta: 'Get Started',
  },
  {
    id: 'promo-card-3',
    icon: '🔗',
    title: 'SD-WAN Enterprise',
    description: 'Optimize network performance across all locations',
    cta: 'Contact Sales',
  },
];

function ToggleSwitch({ checked, onChange }) {
  return (
    <label style={styles.toggle}>
      <input
        style={styles.toggleInput}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        style={{
          ...styles.toggleSlider,
          ...(checked ? styles.toggleSliderOn : {}),
        }}
      >
        <span
          style={{
            ...styles.toggleDot,
            ...(checked ? styles.toggleDotOn : {}),
          }}
        />
      </span>
    </label>
  );
}

export default function ClientNotificationsCenter() {
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(true);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [smsNotificationsEnabled, setSmsNotificationsEnabled] = useState(false);

  // Filter notifications based on active tab
  const filteredNotifications = useMemo(() => {
    if (activeTab === 'all') return notifications;
    return notifications.filter((n) => n.category === activeTab);
  }, [notifications, activeTab]);

  // Get unread count
  const unreadCount = useMemo(() => {
    const activeNotifs =
      activeTab === 'all'
        ? notifications
        : notifications.filter((n) => n.category === activeTab);
    return activeNotifs.filter((n) => !n.read).length;
  }, [notifications, activeTab]);

  // Get count per tab
  const getTabCount = (tabName) => {
    if (tabName === 'all') {
      return notifications.filter((n) => !n.read).length;
    }
    return notifications.filter((n) => n.category === tabName && !n.read).length;
  };

  // Mark single notification as read
  const handleMarkAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  };

  // Mark all as read
  const handleMarkAllAsRead = () => {
    const idsToMark =
      activeTab === 'all'
        ? notifications.filter((n) => !n.read).map((n) => n.id)
        : notifications
            .filter((n) => n.category === activeTab && !n.read)
            .map((n) => n.id);

    setNotifications((prev) =>
      prev.map((n) => (idsToMark.includes(n.id) ? { ...n, read: true } : n))
    );
  };

  // Handle notification action
  const handleNotificationAction = (action) => {
    if (action.callback === 'viewTicket') {
      window.location.href = '/client/tickets';
    } else if (action.callback === 'viewMaintenance') {
      // Handle view maintenance
      alert('Opening maintenance details...');
    } else if (action.callback === 'downloadInvoice') {
      window.location.href = '/client/invoices';
    }
  };

  const getTabs = [
    { id: 'all', label: 'All' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'updates', label: 'Updates' },
    { id: 'system', label: 'System' },
  ];

  return (
    <ClientShell title="Notifications Center">
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>Notifications</div>
          <div style={styles.subtitle}>
            Stay updated on your services, promotions, and account activity
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabsContainer}>
          {getTabs.map((tab) => {
            const count = getTabCount(tab.id);
            return (
              <button
                key={tab.id}
                style={{
                  ...styles.tab,
                  ...(activeTab === tab.id ? styles.tabActive : {}),
                }}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {count > 0 && <span style={styles.badge}>{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Controls */}
        <div style={styles.controlsBar}>
          <div />
          {unreadCount > 0 && (
            <button
              style={styles.markAllButton}
              onClick={handleMarkAllAsRead}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor =
                  styles.markAllButtonHover.backgroundColor;
                e.target.style.borderColor =
                  styles.markAllButtonHover.borderColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1A2332';
                e.target.style.borderColor = '#2A3444';
              }}
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Marketing Promotions (top of Alerts tab or always visible) */}
        {activeTab === 'all' || activeTab === 'marketing' ? (
          <div style={{ marginBottom: '32px' }}>
            {activeTab !== 'marketing' && (
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#FFF', marginBottom: '16px' }}>
                ✨ Featured Services
              </div>
            )}
            {PROMOTIONS.map((promo) => (
              <div
                key={promo.id}
                style={styles.promotionCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor =
                    styles.promotionCardHover.borderColor;
                  e.currentTarget.style.backgroundColor =
                    styles.promotionCardHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#2A3444';
                  e.currentTarget.style.backgroundColor = '#0F1729';
                }}
              >
                <div style={styles.promotionIcon}>{promo.icon}</div>
                <div style={styles.promotionContent}>
                  <div style={styles.promotionTitle}>{promo.title}</div>
                  <div style={styles.promotionDescription}>{promo.description}</div>
                </div>
                <button
                  style={styles.promotionCTA}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor =
                      styles.promotionCTAHover.backgroundColor;
                    e.target.style.transform = styles.promotionCTAHover.transform;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#00D4AA';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {promo.cta}
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {/* Notifications List */}
        {filteredNotifications.length > 0 ? (
          <div style={styles.notificationsList}>
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  ...styles.notificationItem,
                  ...(notification.read
                    ? {}
                    : styles.notificationItemUnread),
                }}
                onMouseEnter={(e) => {
                  if (!notification.read) {
                    e.currentTarget.style.borderColor = '#0057FF';
                    e.currentTarget.style.backgroundColor = '#1A2332';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!notification.read) {
                    e.currentTarget.style.borderColor = '#0057FF';
                    e.currentTarget.style.backgroundColor = '#1A2332';
                  }
                }}
              >
                <div style={styles.notificationIcon}>
                  {notification.icon}
                </div>
                <div style={styles.notificationContent}>
                  <div style={styles.notificationTitle}>
                    {notification.title}
                  </div>
                  <div style={styles.notificationMessage}>
                    {notification.message}
                  </div>
                  <div style={styles.notificationMeta}>
                    <span style={styles.notificationTime}>
                      {notification.timestamp}
                    </span>
                    {notification.action && (
                      <button
                        style={styles.notificationAction}
                        onClick={() => {
                          handleMarkAsRead(notification.id);
                          handleNotificationAction(notification.action);
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor =
                            styles.notificationActionHover.backgroundColor;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#0057FF';
                        }}
                      >
                        {notification.action.label}
                      </button>
                    )}
                  </div>
                </div>
                {!notification.read && <div style={styles.unreadBadge} />}
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateIcon}>🎉</div>
            <div style={styles.emptyStateTitle}>All caught up!</div>
            <div style={styles.emptyStateText}>
              No{' '}
              {activeTab !== 'all'
                ? activeTab
                : ''}
              notifications at the moment.
            </div>
          </div>
        )}

        {/* Notification Preferences */}
        <div style={styles.preferencesSection}>
          <div style={styles.preferencesTitle}>📋 Notification Preferences</div>

          <div
            style={{
              ...styles.preferenceRow,
            }}
          >
            <div>
              <div style={styles.preferenceLabel}>Push Notifications</div>
              <div style={styles.preferenceDescription}>
                Receive notifications in your browser
              </div>
            </div>
            <ToggleSwitch
              checked={pushNotificationsEnabled}
              onChange={setPushNotificationsEnabled}
            />
          </div>

          <div
            style={{
              ...styles.preferenceRow,
            }}
          >
            <div>
              <div style={styles.preferenceLabel}>Email Notifications</div>
              <div style={styles.preferenceDescription}>
                Get important updates via email
              </div>
            </div>
            <ToggleSwitch
              checked={emailNotificationsEnabled}
              onChange={setEmailNotificationsEnabled}
            />
          </div>

          <div
            style={{
              ...styles.preferenceRow,
              ...styles.preferenceRowLast,
            }}
          >
            <div>
              <div style={styles.preferenceLabel}>SMS Notifications</div>
              <div style={styles.preferenceDescription}>
                Critical alerts via text message
              </div>
            </div>
            <ToggleSwitch
              checked={smsNotificationsEnabled}
              onChange={setSmsNotificationsEnabled}
            />
          </div>
        </div>
      </div>
    </ClientShell>
  );
}
